// --------------------------------------------------------------
// Created On: 2026-07-05
// Author: Dimitra Weinstein
//
// Last Modified: 2026-07-17
// Modified By: Dimitra Weinstein
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import React, { Fragment, useMemo } from "react";
import DotLoader from "src/components/DotLoader/DotLoader";
import MessageBubble from "./MessageBubble/MessageBubble";
import Error from "src/components/Error/Error";

// MessageList component for displaying a list of chat messages in the AI agent chat window.
export default function MessageList(props: Props): Component {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const bottomRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (bottomRef !== null && bottomRef.current !== null) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [JSON.stringify(props.messages)]);

  // Memoize the chat messages to prevent unnecessary re-renders.
  const chatMessages = useMemo(() => {
    return props.messages.map((message, i) => (
      <MessageBubble
        key={i}
        messageText={message.messageText !== null ? message.messageText : ""}
        isUserMessage={message.isUserMessage}
      />
    ));
  }, [JSON.stringify(props.messages)]);

  return (
    <Fragment>
      <div ref={containerRef}>
        {chatMessages}
        {props.loading && <DotLoader loading={props.loading} className="mx-4 my-4" />}
        {props.errorMessage && !props.loading && <Error message={props.errorMessage} />}
      </div>
      <div ref={bottomRef} />
    </Fragment>
  );
}

interface Props {
  loading: boolean;
  errorMessage: string;
  messages: ChatMessage[];
}

interface ChatMessage {
  sessionId: string;
  messageText: string | null;
  isUserMessage: boolean;
}
